import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/account-deletion")({
  head: () => ({
    meta: [
      { title: "Delete your Like2Fit account" },
      {
        name: "description",
        content:
          "How to delete your Like2Fit account from inside the app or by emailing support, what data is removed, and what may be retained for legal reasons.",
      },
      { property: "og:title", content: "Delete your Like2Fit account" },
      {
        property: "og:description",
        content:
          "How to delete your Like2Fit account from inside the app or by emailing support, what data is removed, and what may be retained for legal reasons.",
      },
      { property: "og:url", content: "https://like2fit.com/account-deletion" },
    ],
    links: [
      { rel: "canonical", href: "https://like2fit.com/account-deletion" },
    ],
  }),
  component: AccountDeletionPage,
});

const EMAIL = "support@like2fit.com";

function Section({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-10">
      <h2 className="text-xl font-semibold">
        {n}. {title}
      </h2>
      <div className="mt-3 space-y-3 text-[15px] leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function AccountDeletionPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-3xl mx-auto px-5 py-8">
        <Link
          to="/"
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          ← Back
        </Link>

        <h1 className="text-3xl font-semibold mt-6">
          Delete your Like2Fit account
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Last updated June 8, 2026
        </p>

        <div className="mt-6 text-[15px] leading-relaxed">
          <p>
            You can delete your Like2Fit account at any time. This page
            explains how to do it from inside the app, how to request
            deletion by email if you can&apos;t sign in, what data is
            removed, and what we may need to keep for legal reasons.
          </p>
        </div>

        <Section n={1} title="In-app deletion (preferred)">
          <p>
            The easiest way to delete your Like2Fit account is from inside
            the app:
          </p>
          <ol className="list-decimal pl-6 space-y-1">
            <li>Open the Like2Fit app</li>
            <li>Go to Account (bottom-right tab)</li>
            <li>
              Scroll to the bottom and tap <strong>Delete account</strong>
            </li>
            <li>
              Type <strong>DELETE</strong> to confirm
            </li>
            <li>
              Tap <strong>Delete account permanently</strong>
            </li>
          </ol>
          <p>
            Your account is then marked for deletion. You have 30 days to
            recover it by signing in again. After 30 days, all your data is
            permanently removed.
          </p>
        </Section>

        <Section n={2} title="Web-based deletion request (fallback)">
          <p>
            If you no longer have access to the app or can&apos;t sign in:
          </p>
          <ol className="list-decimal pl-6 space-y-1">
            <li>
              Email{" "}
              <a href={`mailto:${EMAIL}`} className="underline">
                {EMAIL}
              </a>{" "}
              from the email address linked to your Like2Fit account
            </li>
            <li>
              Subject line: <strong>&ldquo;Delete my account&rdquo;</strong>
            </li>
            <li>Include your account email in the message body</li>
            <li>
              We will delete your account within 30 days and confirm by email
            </li>
          </ol>
          <p>
            Please note: account deletion is permanent after the 30-day
            recovery window.
          </p>
        </Section>

        <Section n={3} title="What data is deleted">
          <p>
            When your account is deleted, the following data is permanently
            removed:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Your profile (email, name, sign-in credentials)</li>
            <li>
              Your size, height, body shape, gender, and fit preferences
            </li>
            <li>Photos you uploaded for outfit analysis</li>
            <li>AI-generated lookbook images</li>
            <li>Wardrobe items you added</li>
            <li>Saved outfits and viewing history</li>
            <li>Brand fit preferences</li>
            <li>Any other personal data linked to your account</li>
          </ul>
          <p>
            Anonymized analytics data (e.g. counts of outfits analyzed) may
            be retained for service improvement; this data cannot be linked
            back to you after deletion.
          </p>
        </Section>

        <Section n={4} title="Data retained for legal reasons">
          <p>
            We may retain limited information where required by law or for
            fraud prevention (e.g. transaction logs from affiliate partners
            where applicable). This data is kept only as long as legally
            required and is not used to identify you.
          </p>
        </Section>

        <Section n={5} title="Questions">
          <p>
            Email{" "}
            <a href={`mailto:${EMAIL}`} className="underline">
              {EMAIL}
            </a>{" "}
            for help with account deletion or any data privacy question.
          </p>
        </Section>

        <section className="mt-14 border-t pt-6 text-sm text-muted-foreground flex flex-wrap gap-x-4 gap-y-2">
          <span>
            Ratecard Technologies Limited —{" "}
            <a href={`mailto:${EMAIL}`} className="underline">
              {EMAIL}
            </a>
          </span>
          <Link to="/privacy" className="underline">
            Privacy Policy
          </Link>
          <Link to="/terms" className="underline">
            Terms of Service
          </Link>
        </section>
      </div>
    </div>
  );
}
